import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useInterval, useQuery } from '@/hooks';
import { EQueryParam } from '@/types';
import {
  getCurrentTimeState,
  INTERVAL,
  translateTimezone,
  updateCurrent,
  updateTime,
  updateTimeInterval,
} from '@/redux/reducers/time';
import { LOCAL_TZ } from '@/constants';
import { useHeaderFloorsQuery, useTimezoneBuildingQuery } from '@/.generated/graphql';
import { getSingleGraphQLModel } from '@/utils/getSingleGraphQLModel';
import { useFeatureGate } from '@statsig/react-bindings';

/**
 * Hooks for fetching space/room id
 * @returns space/room id
 */
export const useSRZId = (): {
  spaceId?: string;
  roomId?: string;
  zoneId?: string;
  buildingId?: string;
  siteId?: string;
  sensorId?: string;
} => {
  const query = useQuery();
  const spaceId = query.get(EQueryParam.SID) || undefined;
  const roomId = query.get(EQueryParam.RID) || undefined;
  const zoneId = query.get(EQueryParam.ZID) || undefined;
  const buildingId = query.get(EQueryParam.BID) || undefined;
  const siteId = query.get(EQueryParam.SITE_ID) || undefined;
  const sensorId = query.get(EQueryParam.SENSOR_ID) || undefined;

  return useMemo(
    () => ({ buildingId, spaceId, roomId, zoneId, siteId, sensorId }),
    [buildingId, spaceId, roomId, zoneId, siteId, sensorId],
  );
};

/**
 * Hooks for updating the initial timezone and refreshing time every 2 minutes
 */
export const useManageTime = () => {
  const dispatch = useDispatch();
  const { spaceId, buildingId } = useSRZId();
  const { value: medianEnabled } = useFeatureGate('median_presence');
  const timeInitialized = useRef(false);
  const prevTimezoneRef = useRef<string | null>(null);
  const { data: spaceData, loading: spaceLoading } = useHeaderFloorsQuery({
    // skip validates this assertion.

    variables: { spaceId: spaceId! },
    skip: !spaceId,
  });
  const space = getSingleGraphQLModel(spaceData?.floors);

  const { data: buildingData, loading: buildingLoading } = useTimezoneBuildingQuery({
    // skip validates this assertion.

    variables: { buildingId: buildingId! },
    skip: !buildingId,
  });

  const building = buildingData?.building
    ? { id: buildingData.building.id, timezone: buildingData.building.site.timezone || '' }
    : null;

  const timezone = (space ? space.timezone : building?.timezone) || LOCAL_TZ;
  const hasQueryTarget = !!spaceId || !!buildingId;
  const queriesResolved = hasQueryTarget ? !spaceLoading && !buildingLoading : true;

  // Initialize time state once after timezone queries have resolved so the
  // default "yesterday" range is computed from the target org's calendar day.
  // The useTime() hook provides a LOCAL_TZ fallback for render safety while
  // queries are still in-flight, avoiding the null crash without committing
  // a potentially wrong date range to Redux.
  useEffect(() => {
    if (!timeInitialized.current && queriesResolved) {
      dispatch(
        updateTime(getCurrentTimeState(timezone, medianEnabled ? INTERVAL.MINUTE : undefined)),
      );
      timeInitialized.current = true;
      prevTimezoneRef.current = timezone;
    }
  }, [dispatch, timezone, medianEnabled, queriesResolved]);

  // Translate epochs to preserve local wall-clock time when timezone changes
  useEffect(() => {
    if (!timeInitialized.current) return;
    const prevTz = prevTimezoneRef.current;
    if (prevTz && prevTz !== timezone) {
      dispatch(translateTimezone({ from: prevTz, to: timezone }));
    }
    prevTimezoneRef.current = timezone;
  }, [dispatch, timezone]);

  // Update interval if medianEnabled resolves asynchronously after init
  useEffect(() => {
    if (timeInitialized.current && medianEnabled) {
      dispatch(updateTimeInterval(INTERVAL.MINUTE));
    }
  }, [dispatch, medianEnabled]);

  // WARNING: this is extremely fragile. messing up the stability
  // of this function will cause infinite loops of metrics requests.
  const refreshStats = useCallback(() => {
    if (!space) return;
    // Updating the time will invalidate all queries that are based on time (including stats queries)
    dispatch(
      updateCurrent({
        currentDate: new Date(),
      }),
    );
  }, [dispatch, space]);

  useInterval(120 * 1000, refreshStats, [refreshStats]);
};
